import { useEffect, useRef, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router"
import { client } from "@client/client"
import { CLASS_LEVELS } from "@client/lib/class-levels"
import { getErrorMessage } from "@client/lib/errors"
import { NIGERIAN_STATES } from "@client/lib/nigerian-states"
import { uploadFile } from "@client/lib/uploads"
import { getAcademicAssessment } from "@shared/academic"
import { toast } from "sonner"
import { useHasPermission } from "./session-context"
import { Select } from "./components/select"
import { DatePicker } from "./components/date-picker"
import { Portal } from "./components/portal"
import { Modal, ModalActions, ModalButton } from "./components/modal"
import { ConfirmModal } from "./components/confirm-modal"
import { SponsorMatchModal } from "./components/sponsor-match-modal"
import { DetailPageSkeleton } from "./components/skeleton"
import { SplitActionButton } from "./components/split-action-button"
import { ActivityTimeline } from "./components/activity-timeline"
import { GuardianFamilyCard } from "./components/guardian-family-card"
import { AdminActivityEntityType } from "@shared/admin-activities"

type ChildStatus = "nominated" | "unverified" | "verified" | "sponsored" | "graduated"

interface Child {
  id: string
  code: string
  firstName: string
  lastName: string
  dateOfBirth: string
  gender: string
  preferredName: string | null
  classLevel: string
  school: string
  schoolId: string | null
  state: string
  lga: string | null
  status: ChildStatus
  photoUrl: string | null
  documents: {
    name: string
    key: string
    url: string
    purpose: "child-document" | "child-report"
    contentType: string
    sizeBytes: number
    uploadedAt: string
  }[]
  sponsorId: string | null
  sponsorName: string | null
  sponsoredAt: string | null
  activeSponsors: { id: string; name: string; matchedAt: string }[]
  nominatedBy: string | null
  nominatorContact: string | null
  notes: string | null
  guardianName: string | null
  guardianPhone: string | null
  guardianEmail: string | null
  guardianRelationship: string | null
  householdSize: number | null
  address: string | null
  familyCircumstances: string | null
  needsSponsorship: boolean
  sponsorshipNotes: string | null
  createdAt: string
  updatedAt: string
}

interface SchoolOption {
  id: string
  code: string
  name: string
}

interface LinkedSchool extends SchoolOption {
  city: string
  state: string
}

const NO_SCHOOL = "No school assigned"

interface Subject {
  id: string
  name: string
  active: boolean
}

interface SubjectProfile {
  id: string
  childId: string
  subject: Subject
  score: number | null
  legacyAssessment: string | null
  proficiency: string | null
  strengths: string | null
  needsAttention: string | null
  grade: string | null
  teacherNote: string | null
  createdAt: string
  updatedAt: string
}

interface SubjectResult {
  id: string
  termId: string
  subject: Subject
  score: number | null
  legacyAssessment: string | null
  proficiency: string | null
  grade: string | null
  strengths: string | null
  needsAttention: string | null
  teacherNote: string | null
  createdAt: string
  updatedAt: string
}

interface AcademicTerm {
  id: string
  childId: string
  session: string
  term: string
  attendance: number | null
  position: string | null
  teacherSummary: string | null
  results: SubjectResult[]
  createdAt: string
  updatedAt: string
}

type ResultDraft = {
  score: string
  strengths: string
  needsAttention: string
  teacherNote: string
}

const EMPTY_RESULT_DRAFT: ResultDraft = {
  score: "",
  strengths: "",
  needsAttention: "",
  teacherNote: "",
}

type SubjectDraft = {
  score: string
  strengths: string | null
  needsAttention: string | null
  teacherNote: string | null
}

const STATUS_COLORS: Record<ChildStatus, { bg: string; text: string }> = {
  nominated: { bg: "bg-orange-100", text: "text-orange-700" },
  unverified: { bg: "bg-red-100", text: "text-red-700" },
  verified: { bg: "bg-blue-100", text: "text-blue-700" },
  sponsored: { bg: "bg-green-100", text: "text-green-700" },
  graduated: { bg: "bg-gray-100", text: "text-gray-700" },
}

type Tab = "profile" | "academics" | "sponsorship" | "documents" | "timeline"

const VALID_TABS: readonly Tab[] = ["profile", "academics", "sponsorship", "documents", "timeline"]

function getTabFromSearch(value: string | null): Tab {
  return VALID_TABS.includes(value as Tab) ? value as Tab : "profile"
}

const UPDATE_TYPES = [
  "Term report",
  "Attendance record",
  "Health note",
  "Sponsor letter",
  "Other document",
] as const

const ACADEMIC_SESSIONS = Array.from({ length: 11 }, (_, index) => {
  const startYear = new Date().getFullYear() - index
  return `${startYear}/${startYear + 1}`
})

const TERM_ORDER: Record<string, number> = {
  "First Term": 0,
  "Second Term": 1,
  "Third Term": 2,
}

function sortAcademicTerms(terms: AcademicTerm[]) {
  return [...terms].sort((a, b) => {
    const sessionOrder = (Number(b.session.match(/^\d{4}/)?.[0]) || -1) - (Number(a.session.match(/^\d{4}/)?.[0]) || -1)
    if (sessionOrder !== 0) return sessionOrder

    const termOrder = (TERM_ORDER[a.term] ?? 99) - (TERM_ORDER[b.term] ?? 99)
    if (termOrder !== 0) return termOrder

    return b.createdAt.localeCompare(a.createdAt)
  })
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toISOString().split("T")[0] ?? dateString
}

function calculateAge(dateOfBirth: string): number {
  const today = new Date()
  const dob = new Date(dateOfBirth)
  let age = today.getFullYear() - dob.getFullYear()
  const monthDiff = today.getMonth() - dob.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--
  }
  return age
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function MessageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  )
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  )
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  )
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  )
}

function FileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
    </svg>
  )
}

export function ChildDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const canView = useHasPermission("children")
  const canMutate = useHasPermission("children_mutate")
  const [child, setChild] = useState<Child | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>(() => getTabFromSearch(searchParams.get("tab")))
  const [showEditModal, setShowEditModal] = useState(false)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [messageForm, setMessageForm] = useState({ subject: "", message: "" })
  const [sendingMessage, setSendingMessage] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showSponsorMatchModal, setShowSponsorMatchModal] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [pendingStatus, setPendingStatus] = useState<ChildStatus>("nominated")
  const [saving, setSaving] = useState(false)
  const [activityRefreshKey, setActivityRefreshKey] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [editPhotoKey, setEditPhotoKey] = useState<string | null>(null)
  const [editPhotoPreview, setEditPhotoPreview] = useState<string | null>(null)
  const [editPhotoUploading, setEditPhotoUploading] = useState(false)
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [schools, setSchools] = useState<SchoolOption[]>([])
  const [linkedSchool, setLinkedSchool] = useState<LinkedSchool | null>(null)
  const [subjectProfiles, setSubjectProfiles] = useState<SubjectProfile[]>([])
  const [subjectDrafts, setSubjectDrafts] = useState<Record<string, SubjectDraft>>({})
  const [selectedSubjectId, setSelectedSubjectId] = useState("")
  const [newSubjectName, setNewSubjectName] = useState("")
  const [savingSubject, setSavingSubject] = useState(false)
  const [academicTerms, setAcademicTerms] = useState<AcademicTerm[]>([])
  const [selectedAcademicTermId, setSelectedAcademicTermId] = useState("")
  const [showTermForm, setShowTermForm] = useState(false)
  const [termForm, setTermForm] = useState({ session: ACADEMIC_SESSIONS[0] ?? "", term: "First Term" })
  const [savingTerm, setSavingTerm] = useState(false)
  const [termDrafts, setTermDrafts] = useState<Record<string, { attendance: string; position: string; teacherSummary: string }>>({})
  const [selectedAcademicSubjectId, setSelectedAcademicSubjectId] = useState("")
  const [showNewAcademicSubject, setShowNewAcademicSubject] = useState(false)
  const [resultDrafts, setResultDrafts] = useState<Record<string, ResultDraft>>({})
  const [addResultDraft, setAddResultDraft] = useState<ResultDraft>(EMPTY_RESULT_DRAFT)
  const [editingAcademicTermId, setEditingAcademicTermId] = useState<string | null>(null)
  const [showResultModal, setShowResultModal] = useState(false)
  const [editingResultId, setEditingResultId] = useState<string | null>(null)
  const [resultToDelete, setResultToDelete] = useState<SubjectResult | null>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const editPhotoInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setActiveTab(getTabFromSearch(searchParams.get("tab")))
  }, [searchParams])

  const handleTabChange = (tab: Tab) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set("tab", tab)
    setSearchParams(nextParams, { replace: true })
  }

  const [uploadForm, setUploadForm] = useState({
    updateType: "Term report",
    fileName: "",
  })

  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "male",
    classLevel: "Primary 1",
    school: "",
    schoolId: null as string | null,
    state: "Edo",
    lga: "",
    notes: "",
    preferredName: "",
    status: "nominated" as ChildStatus,
    nominatedBy: "",
    nominatorContact: "",
    guardianName: "",
    guardianPhone: "",
    guardianEmail: "",
    guardianRelationship: "",
    householdSize: "",
    address: "",
    familyCircumstances: "",
    needsSponsorship: true,
    sponsorshipNotes: "",
  })

  const loadSubjects = async () => {
    try {
      const res = await client.api.admin.subjects.$get()
      const body = await res.json()
      if (res.ok && "data" in body) setSubjects(body.data)
    } catch {
      // Subject profiles remain optional if the catalog is unavailable.
    }
  }

  const loadSchools = async () => {
    try {
      const res = await client.api.admin.schools.$get({ query: { limit: "100" } })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setSchools(body.data.items as SchoolOption[])
      }
    } catch {
      // School assignment remains optional if the school catalog is unavailable.
    }
  }

  const loadLinkedSchool = async (schoolId: string) => {
    try {
      const res = await client.api.admin.schools[":id"].$get({ param: { id: schoolId } })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setLinkedSchool({
          id: body.data.id,
          code: body.data.code,
          name: body.data.name,
          city: body.data.city,
          state: body.data.state,
        })
      }
    } catch {
      // The child profile remains available if the linked school cannot be loaded.
    }
  }

  const loadSubjectProfiles = async (childId: string) => {
    try {
      const res = await client.api.admin.children[":id"].subjects.$get({ param: { id: childId } })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setSubjectProfiles(body.data)
        setSubjectDrafts(
          Object.fromEntries(
            body.data.map((profile) => [profile.subject.id, {
              score: profile.score?.toString() ?? "",
              strengths: profile.strengths,
              needsAttention: profile.needsAttention,
              teacherNote: profile.teacherNote,
            }]),
          ),
        )
      }
    } catch {
      // Subject profiles remain optional if the child profile has none yet.
    }
  }

  const loadAcademicTerms = async (childId: string) => {
    try {
      const res = await client.api.admin.children[":id"]["academic-terms"].$get({ param: { id: childId } })
      const body = await res.json()
      if (res.ok && "data" in body) {
        setAcademicTerms(sortAcademicTerms(body.data))
        setSelectedAcademicTermId((current) => current || body.data[body.data.length - 1]?.id || "")
        setTermDrafts(
          Object.fromEntries(body.data.map((academicTerm) => [academicTerm.id, {
            attendance: academicTerm.attendance?.toString() ?? "",
            position: academicTerm.position ?? "",
            teacherSummary: academicTerm.teacherSummary ?? "",
          }])),
        )
        setResultDrafts(
          Object.fromEntries(
            body.data.flatMap((academicTerm) => academicTerm.results.map((result) => [result.id, {
              score: result.score?.toString() ?? "",
              strengths: result.strengths ?? "",
              needsAttention: result.needsAttention ?? "",
              teacherNote: result.teacherNote ?? "",
            }])),
          ),
        )
      }
    } catch {
      // Academic terms remain optional until the first term is created.
    }
  }

  const fetchChild = async () => {
    if (!id) return
    try {
      const res = await client.api.admin.children[":id"].$get({ param: { id } })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        const c = body.data as Child
        setChild(c)
          setLinkedSchool(null)
          if (c.schoolId) void loadLinkedSchool(c.schoolId)
          setEditForm({
            firstName: c.firstName,
            lastName: c.lastName,
            dateOfBirth: c.dateOfBirth.split("T")[0] ?? "",
            gender: c.gender,
            classLevel: c.classLevel,
            school: c.school,
            schoolId: c.schoolId,
            state: c.state,
            lga: c.lga ?? "",
            notes: c.notes ?? "",
            preferredName: c.preferredName ?? "",
            status: c.status,
            nominatedBy: c.nominatedBy ?? "",
            nominatorContact: c.nominatorContact ?? "",
            guardianName: c.guardianName ?? "",
            guardianPhone: c.guardianPhone ?? "",
            guardianEmail: c.guardianEmail ?? "",
            guardianRelationship: c.guardianRelationship ?? "",
            householdSize: c.householdSize?.toString() ?? "",
            address: c.address ?? "",
            familyCircumstances: c.familyCircumstances ?? "",
            needsSponsorship: c.needsSponsorship,
            sponsorshipNotes: c.sponsorshipNotes ?? "",
          })
          if (editPhotoPreview) URL.revokeObjectURL(editPhotoPreview)
          setEditPhotoKey(null)
          setEditPhotoPreview(null)
          void loadSubjectProfiles(c.id)
          void loadAcademicTerms(c.id)
      }
    } catch {
      setError("Failed to load child")
    } finally {
      setLoading(false)
    }
  }

  const reloadPageData = async () => {
    if (!id) return
    await Promise.all([fetchChild(), loadSubjectProfiles(id), loadAcademicTerms(id)])
    setActivityRefreshKey((key) => key + 1)
  }

  useEffect(() => {
    if (canView) {
      void fetchChild()
      void loadSubjects()
      void loadSchools()
    } else {
      setLoading(false)
    }
  }, [id, canView])

  const handleEditPhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file || editPhotoUploading) return

    setEditPhotoUploading(true)
    try {
      const uploaded = await uploadFile(file, "child-photo")
      if (editPhotoPreview) URL.revokeObjectURL(editPhotoPreview)
      setEditPhotoKey(uploaded.key)
      setEditPhotoPreview(URL.createObjectURL(file))
      toast.success("Photo uploaded")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to upload photo")
    } finally {
      setEditPhotoUploading(false)
    }
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !canMutate) return
    setSaving(true)
    try {
      const res = await client.api.admin.children[":id"].$patch({
        param: { id },
        json: {
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          dateOfBirth: new Date(editForm.dateOfBirth).toISOString(),
          gender: editForm.gender as "male" | "female",
          preferredName: editForm.preferredName || null,
          classLevel: editForm.classLevel,
          school: editForm.school,
          schoolId: editForm.schoolId || (editForm.school ? undefined : null),
          state: editForm.state,
          lga: editForm.lga || null,
          status: editForm.status,
          ...(editPhotoKey ? { photoKey: editPhotoKey } : {}),
          nominatedBy: editForm.nominatedBy || null,
          nominatorContact: editForm.nominatorContact || null,
          notes: editForm.notes || null,
          guardianName: editForm.guardianName || null,
          guardianPhone: editForm.guardianPhone || null,
          guardianEmail: editForm.guardianEmail || null,
          guardianRelationship: editForm.guardianRelationship || null,
          householdSize: editForm.householdSize ? Number(editForm.householdSize) : null,
          address: editForm.address || null,
          familyCircumstances: editForm.familyCircumstances || null,
          needsSponsorship: editForm.needsSponsorship,
          sponsorshipNotes: editForm.sponsorshipNotes || null,
        },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Child updated successfully")
      if ("data" in body) setChild(body.data as Child)
      setShowEditModal(false)
      if (editPhotoPreview) URL.revokeObjectURL(editPhotoPreview)
      setEditPhotoPreview(null)
      await reloadPageData()
    } catch {
      toast.error("Failed to update child")
    } finally {
      setSaving(false)
    }
  }

  const handleStatusChange = async (status: ChildStatus) => {
    if (!id || !canMutate || !child || status === child.status) return
    setSaving(true)
    try {
      const res = await client.api.admin.children[":id"].$patch({
        param: { id },
        json: { status },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success(`Child status updated to ${status}`)
      await reloadPageData()
    } catch {
      toast.error("Failed to update child status")
    } finally {
      setSaving(false)
    }
  }

  const openStatusModal = () => {
    if (!child) return
    setPendingStatus(child.status)
    setShowStatusModal(true)
  }

  const handleCreateSubject = async () => {
    const name = newSubjectName.trim()
    if (!name) return
    try {
      const res = await client.api.admin.subjects.$post({ json: { name } })
      const body = await res.json()
      if (!res.ok || !("data" in body)) {
        toast.error(getErrorMessage(body))
        return
      }
      setSubjects((current) => [...current, body.data].sort((a, b) => a.name.localeCompare(b.name)))
      setSelectedSubjectId(body.data.id)
      setSelectedAcademicSubjectId(body.data.id)
      setNewSubjectName("")
      toast.success("Subject created")
      await Promise.all([loadSubjects(), reloadPageData()])
    } catch {
      toast.error("Failed to create subject")
    }
  }

  const handleCreateAcademicTerm = async () => {
    if (!id || !termForm.session.trim() || !termForm.term.trim()) return
    setSavingTerm(true)
    try {
      const res = await client.api.admin.children[":id"]["academic-terms"].$post({
        param: { id },
        json: { session: termForm.session.trim(), term: termForm.term.trim() },
      })
      const body = await res.json()
      if (!res.ok || !("data" in body)) {
        toast.error(getErrorMessage(body))
        return
      }
      setAcademicTerms((current) => sortAcademicTerms([...current, body.data]))
      setTermDrafts((current) => ({ ...current, [body.data.id]: { attendance: "", position: "", teacherSummary: "" } }))
      setSelectedAcademicTermId(body.data.id)
      setShowTermForm(false)
      setTermForm({ session: ACADEMIC_SESSIONS[0] ?? "", term: "First Term" })
      toast.success("Academic term created")
      await reloadPageData()
    } catch {
      toast.error("Failed to create academic term")
    } finally {
      setSavingTerm(false)
    }
  }

  const handleUpdateAcademicTerm = async (term: AcademicTerm, updates: Partial<Pick<AcademicTerm, "attendance" | "position" | "teacherSummary">>) => {
    if (!id) return
    try {
      const res = await client.api.admin.children[":id"]["academic-terms"][":termId"].$patch({
        param: { id, termId: term.id },
        json: updates,
      })
      const body = await res.json()
      if (!res.ok || !("data" in body)) {
        toast.error(getErrorMessage(body))
        return
      }
      setAcademicTerms((current) => current.map((item) => item.id === term.id ? body.data : item))
      setTermDrafts((current) => ({ ...current, [term.id]: {
        attendance: body.data.attendance?.toString() ?? "",
        position: body.data.position ?? "",
        teacherSummary: body.data.teacherSummary ?? "",
      } }))
      setEditingAcademicTermId(null)
      toast.success("Term updated")
      await reloadPageData()
    } catch {
      toast.error("Failed to update term")
    }
  }

  const handleSaveAcademicTerm = async () => {
    if (!editingAcademicTerm) return
    const draft = termDrafts[editingAcademicTerm.id] ?? { attendance: "", position: "", teacherSummary: "" }
    const attendance = draft.attendance.trim() === "" ? null : Number(draft.attendance)
    if (attendance !== null && (!Number.isInteger(attendance) || attendance < 0 || attendance > 100)) {
      toast.error("Attendance must be a whole number from 0 to 100")
      return
    }
    await handleUpdateAcademicTerm(editingAcademicTerm, {
      attendance,
      position: draft.position.trim() || null,
      teacherSummary: draft.teacherSummary.trim() || null,
    })
  }

  const handleAddSubjectResult = async () => {
    if (!id || !selectedAcademicTermId || !selectedAcademicSubjectId) return
    const score = addResultDraft.score.trim() === "" ? null : Number(addResultDraft.score)
    if (score === null || !Number.isInteger(score) || score < 0 || score > 100) {
      toast.error("Score is required and must be a whole number from 0 to 100")
      return
    }
    try {
      const res = await client.api.admin.children[":id"]["academic-terms"][":termId"].results.$post({
        param: { id, termId: selectedAcademicTermId },
        json: {
          subjectId: selectedAcademicSubjectId,
          score,
          strengths: addResultDraft.strengths || null,
          needsAttention: addResultDraft.needsAttention || null,
          teacherNote: addResultDraft.teacherNote || null,
        },
      })
      const body = await res.json()
      if (!res.ok || !("data" in body)) {
        toast.error(getErrorMessage(body))
        return
      }
      setAcademicTerms((current) => current.map((term) => term.id === selectedAcademicTermId
        ? { ...term, results: [...term.results, body.data].sort((a, b) => a.subject.name.localeCompare(b.subject.name)) }
        : term))
      setResultDrafts((current) => ({ ...current, [body.data.id]: { score: "", strengths: "", needsAttention: "", teacherNote: "" } }))
      setSelectedAcademicSubjectId("")
      setAddResultDraft(EMPTY_RESULT_DRAFT)
      setShowResultModal(false)
      setShowNewAcademicSubject(false)
      toast.success("Subject added to term")
      await reloadPageData()
    } catch {
      toast.error("Failed to add subject result")
    }
  }

  const handleSaveSubjectResult = async (result: SubjectResult) => {
    if (!id) return
    const draft = resultDrafts[result.id] ?? { score: "", strengths: "", needsAttention: "", teacherNote: "" }
    const score = draft.score.trim() === "" ? null : Number(draft.score)
    if (score !== null && (!Number.isInteger(score) || score < 0 || score > 100)) {
      toast.error("Score must be a whole number from 0 to 100")
      return
    }
    try {
      const res = await client.api.admin.children[":id"]["academic-terms"][":termId"].results[":resultId"].$patch({
        param: { id, termId: result.termId, resultId: result.id },
        json: {
          score,
          strengths: draft.strengths || null,
          needsAttention: draft.needsAttention || null,
          teacherNote: draft.teacherNote || null,
        },
      })
      const body = await res.json()
      if (!res.ok || !("data" in body)) {
        toast.error(getErrorMessage(body))
        return
      }
      setAcademicTerms((current) => current.map((term) => term.id === result.termId
        ? { ...term, results: term.results.map((item) => item.id === result.id ? body.data : item) }
        : term))
      setShowResultModal(false)
      setEditingResultId(null)
      toast.success("Subject result saved")
      await reloadPageData()
    } catch {
      toast.error("Failed to save subject result")
    }
  }

  const handleDeleteSubjectResult = async (result: SubjectResult) => {
    if (!id) return
    setResultToDelete(null)
    try {
      const res = await client.api.admin.children[":id"]["academic-terms"][":termId"].results[":resultId"].$delete({
        param: { id, termId: result.termId, resultId: result.id },
      })
      if (!res.ok) {
        toast.error(getErrorMessage(await res.json()))
        return
      }
      setAcademicTerms((current) => current.map((term) => term.id === result.termId
        ? { ...term, results: term.results.filter((item) => item.id !== result.id) }
        : term))
      toast.success("Subject result removed")
      await reloadPageData()
    } catch {
      toast.error("Failed to remove subject result")
    }
  }

  const handleSubjectSave = async (subjectId: string) => {
    if (!id) return
    const draft = subjectDrafts[subjectId] ?? {
      score: "",
      strengths: null,
      needsAttention: null,
      teacherNote: null,
    }
    const score = draft.score.trim() === "" ? null : Number(draft.score)
    if (score !== null && (!Number.isInteger(score) || score < 0 || score > 100)) {
      toast.error("Score must be a whole number from 0 to 100")
      return
    }

    setSavingSubject(true)
    try {
      const res = await client.api.admin.children[":id"].subjects.$post({
        param: { id },
        json: {
          subjectId,
          score,
          strengths: draft.strengths || null,
          needsAttention: draft.needsAttention || null,
          teacherNote: draft.teacherNote || null,
        },
      })
      const body = await res.json()
      if (!res.ok || !("data" in body)) {
        toast.error(getErrorMessage(body))
        return
      }
      setSubjectProfiles((current) => {
        const withoutCurrent = current.filter((profile) => profile.subject.id !== subjectId)
        return [...withoutCurrent, body.data].sort((a, b) => a.subject.name.localeCompare(b.subject.name))
      })
      setSelectedSubjectId("")
      toast.success("Subject profile saved")
      await reloadPageData()
    } catch {
      toast.error("Failed to save subject profile")
    } finally {
      setSavingSubject(false)
    }
  }

  const handleSubjectDelete = async (subjectId: string) => {
    if (!id) return
    try {
      const res = await client.api.admin.children[":id"].subjects[":subjectId"].$delete({
        param: { id, subjectId },
      })
      if (!res.ok) {
        const body = await res.json()
        toast.error(getErrorMessage(body))
        return
      }
      setSubjectProfiles((current) => current.filter((profile) => profile.subject.id !== subjectId))
      setSubjectDrafts((current) => {
        const next = { ...current }
        delete next[subjectId]
        return next
      })
      toast.success("Subject removed")
      await reloadPageData()
    } catch {
      toast.error("Failed to remove subject")
    }
  }

  const updateSubjectDraft = (subjectId: string, field: keyof SubjectDraft, value: string) => {
    setSubjectDrafts((current) => ({
      ...current,
      [subjectId]: {
        ...(current[subjectId] ?? {
          score: "",
          strengths: null,
          needsAttention: null,
          teacherNote: null,
        }),
        [field]: value,
      },
    }))
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      if (editPhotoPreview) URL.revokeObjectURL(editPhotoPreview)
      setEditPhotoPreview(null)
      setEditPhotoKey(null)
      setShowEditModal(false)
      setShowMessageModal(false)
      setShowUploadModal(false)
    }
  }

  const handleMessageSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!id || !child?.guardianEmail || !messageForm.subject.trim() || !messageForm.message.trim()) return

    setSendingMessage(true)
    try {
      const res = await client.api.admin.children[":id"].message.$post({
        param: { id },
        json: {
          subject: messageForm.subject.trim(),
          message: messageForm.message.trim(),
        },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Message sent to guardian")
      setShowMessageModal(false)
      setMessageForm({ subject: "", message: "" })
      setActivityRefreshKey((key) => key + 1)
    } catch {
      toast.error("Failed to send message")
    } finally {
      setSendingMessage(false)
    }
  }

  const handleUploadFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file || !id || !child || uploading) return

    setUploadForm((current) => ({ ...current, fileName: file.name }))
    setUploading(true)
    try {
      const uploaded = await uploadFile(file, "child-report")
      const res = await client.api.admin.children[":id"].$patch({
        param: { id },
        json: {
          documents: [
            ...child.documents.map(({ url: _url, ...document }) => document),
            { ...uploaded, purpose: "child-report" as const },
          ],
        },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      if ("data" in body) setChild(body.data as Child)
      toast.success(`${uploadForm.updateType} uploaded successfully`)
      setShowUploadModal(false)
      setUploadForm({ updateType: "Term report", fileName: "" })
      await reloadPageData()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to upload document")
    } finally {
      setUploading(false)
    }
  }

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Child</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view this child.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <DetailPageSkeleton />
  }

  if (error || !child) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate("/admin/children")}
          className="inline-flex items-center gap-1 text-sm font-medium text-ink/60 hover:text-ink"
        >
          <BackIcon className="h-4 w-4" />
          Back
        </button>
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error || "Child not found"}</p>
        </div>
      </div>
    )
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: "profile", label: "Profile" },
    { key: "academics", label: "Academics" },
    { key: "sponsorship", label: "Sponsorship" },
    { key: "documents", label: "Documents" },
    { key: "timeline", label: "Timeline" },
  ]
  const schoolOptions = [NO_SCHOOL, ...schools.map((school) => `${school.code} · ${school.name}`)]
  const selectedSchool = schools.find((school) => school.id === editForm.schoolId)
  const selectedSchoolLabel = selectedSchool
    ? `${selectedSchool.code} · ${selectedSchool.name}`
    : editForm.school || NO_SCHOOL

  const age = calculateAge(child.dateOfBirth)
  const canMatchChild = child.status === "verified" || child.status === "sponsored"
  const selectedAcademicTerm = academicTerms.find((term) => term.id === selectedAcademicTermId) ?? null
  const selectedTermDraft = selectedAcademicTerm ? termDrafts[selectedAcademicTerm.id] ?? { attendance: "", position: "", teacherSummary: "" } : null
  const editingAcademicTerm = academicTerms.find((term) => term.id === editingAcademicTermId) ?? null
  const editingResult = selectedAcademicTerm?.results.find((result) => result.id === editingResultId) ?? null
  const scoredTermResults = selectedAcademicTerm?.results.filter((result) => result.score !== null) ?? []
  const averageScore = scoredTermResults.length > 0
    ? Math.round(scoredTermResults.reduce((total, result) => total + (result.score ?? 0), 0) / scoredTermResults.length)
    : null
  const averageAssessment = getAcademicAssessment(averageScore)

  const renderAcademicTerms = () => (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-ink">Academic Terms</h2>
          <p className="mt-1 text-sm text-ink/60">Select a term to view its subjects and results.</p>
        </div>
        {canMutate && (
          <button
            type="button"
            onClick={() => setShowTermForm(true)}
            className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900"
          >
            Add term
          </button>
        )}
      </div>

      {academicTerms.length === 0 ? (
        <div className="rounded-[10px] border border-gold-500 bg-white p-6 text-center">
          <p className="text-sm text-ink/60">No academic terms recorded yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortAcademicTerms(academicTerms).map((term) => {
            const isExpanded = term.id === selectedAcademicTermId
            const scoredResults = term.results.filter((result) => result.score !== null)
            const termAverage = scoredResults.length > 0
              ? Math.round(scoredResults.reduce((total, result) => total + (result.score ?? 0), 0) / scoredResults.length)
              : null
            const termAssessment = getAcademicAssessment(termAverage)

            return (
              <div key={term.id} className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  onClick={() => setSelectedAcademicTermId(isExpanded ? "" : term.id)}
                  className="flex w-full items-center justify-between gap-4 p-5 text-left transition-colors hover:bg-cream-50"
                >
                  <div className="min-w-0">
                    <h3 className="font-display text-lg font-bold text-forest-700">{term.session} · {term.term}</h3>
                    <p className="mt-1 truncate text-sm text-ink/60">{child.school || NO_SCHOOL} · {child.classLevel}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-4">
                    <div className="hidden text-right sm:block">
                      <p className="text-sm font-medium text-ink">{termAverage === null ? "No scores" : `${termAverage} (${termAssessment.grade})`}</p>
                      <p className="text-xs text-ink/50">{term.results.length} subject{term.results.length === 1 ? "" : "s"}</p>
                    </div>
                    <svg className={`h-5 w-5 text-ink/50 transition-transform ${isExpanded ? "rotate-180" : ""}`} viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </button>

                <div
                  className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${isExpanded ? "grid-rows-[1fr] opacity-100" : "pointer-events-none grid-rows-[0fr] opacity-0"}`}
                  aria-hidden={!isExpanded}
                >
                  <div className="min-h-0 overflow-hidden">
                    <div className="border-t border-gold-500/40 p-5">
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="rounded-lg bg-cream-100 p-3">
                        <p className="text-xs font-medium text-ink/60">Average score</p>
                        <p className="mt-1 text-lg font-semibold text-ink">{termAverage === null ? "—" : `${termAverage}/100`}</p>
                      </div>
                      <div className="rounded-lg bg-cream-100 p-3">
                        <p className="text-xs font-medium text-ink/60">Attendance</p>
                        <p className="mt-1 text-lg font-semibold text-ink">{term.attendance === null ? "—" : `${term.attendance}%`}</p>
                      </div>
                      <div className="rounded-lg bg-cream-100 p-3">
                        <p className="text-xs font-medium text-ink/60">Position</p>
                        <p className="mt-1 text-lg font-semibold text-ink">{term.position || "—"}</p>
                      </div>
                    </div>

                    {canMutate && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button type="button" onClick={() => setEditingAcademicTermId(term.id)} className="rounded-full border border-forest-700 px-4 py-2 text-sm font-medium text-forest-700 hover:bg-cream-100">
                          Edit term
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedAcademicTermId(term.id)
                            setEditingResultId(null)
                            setSelectedAcademicSubjectId("")
                            setAddResultDraft(EMPTY_RESULT_DRAFT)
                            setShowNewAcademicSubject(false)
                            setShowResultModal(true)
                          }}
                          className="rounded-full bg-forest-950 px-4 py-2 text-sm font-medium text-white hover:bg-forest-900"
                        >
                          Add subject result
                        </button>
                      </div>
                    )}

                    <div className="mt-5 space-y-3">
                      {term.results.length === 0 ? (
                        <p className="rounded-lg bg-cream-100 p-4 text-sm text-ink/60">No subjects recorded for this term.</p>
                      ) : term.results.map((result) => (
                        <div key={result.id} className="flex flex-col gap-3 rounded-lg border border-gold-500/40 p-4 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <h4 className="font-medium text-ink">{result.subject.name}</h4>
                            <p className="mt-1 text-sm text-ink/60">
                              {result.score === null ? "Not yet scored" : `${result.score}/100 · ${result.grade} · ${result.proficiency}`}
                            </p>
                            {result.teacherNote && <p className="mt-2 truncate text-xs text-ink/50">{result.teacherNote}</p>}
                          </div>
                          {canMutate && (
                            <div className="flex shrink-0 gap-2">
                              <button type="button" onClick={() => { setSelectedAcademicTermId(term.id); setEditingResultId(result.id); setShowResultModal(true) }} className="rounded-full border border-forest-700 px-3 py-1.5 text-xs font-medium text-forest-700 hover:bg-cream-100">
                                Edit
                              </button>
                              <button type="button" onClick={() => setResultToDelete(result)} className="rounded-full border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {term.teacherSummary && (
                      <div className="mt-5 rounded-lg bg-cream-100 p-4">
                        <p className="text-xs font-medium uppercase tracking-wide text-ink/50">Teacher summary</p>
                        <p className="mt-2 whitespace-pre-wrap text-sm text-ink/70">{term.teacherSummary}</p>
                      </div>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <button
          onClick={() => navigate("/admin/children")}
          className="inline-flex w-fit items-center gap-1 text-sm font-medium text-ink/60 hover:text-ink"
        >
          <BackIcon className="h-4 w-4" />
          Back
        </button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            {child.photoUrl ? (
              <img
                src={child.photoUrl}
                alt={`${child.firstName} ${child.lastName}`}
                className="h-16 w-16 rounded-full object-cover"
              />
            ) : (
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-forest-950 text-lg font-bold text-white">
                {child.firstName[0]}{child.lastName[0]}
              </span>
            )}
            <div>
              <p className="text-xs uppercase tracking-wider text-ink/50">Child · {child.code}</p>
              <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">
                {child.firstName} {child.lastName}
              </h1>
              <p className="mt-1 text-sm text-ink/60">
                {child.gender === "male" ? "Male" : "Female"} · Age {age} · {child.classLevel} · {child.school || NO_SCHOOL}
              </p>
              <span
                className={`mt-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                  STATUS_COLORS[child.status].bg
                } ${STATUS_COLORS[child.status].text}`}
              >
                {child.status}
              </span>
            </div>
          </div>

          {canMutate && (
            <SplitActionButton
              label="Message guardian"
              icon={<MessageIcon className="h-4 w-4" />}
              onClick={() => setShowMessageModal(true)}
              items={[
                {
                  label: "Upload update",
                  icon: <UploadIcon className="h-4 w-4" />,
                  onClick: () => setShowUploadModal(true),
                },
                {
                  label: "Edit record",
                  icon: <EditIcon className="h-4 w-4" />,
                  onClick: () => setShowEditModal(true),
                },
                { label: "Update status", onClick: openStatusModal },
              ]}
            />
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-gold-500/30">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? "border-forest-950 text-forest-950"
                : "border-transparent text-ink/60 hover:text-ink"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "profile" && (
        <div className="space-y-6">
          {/* Identity */}
          <div className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Identity</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="text-xs text-ink/50">Basic Summary</p>
                <p className="text-sm font-medium text-ink">
                  {child.status === "sponsored" ? "Sponsored by " + child.sponsorName : child.status === "nominated" ? "Nominated by " + (child.nominatedBy ?? "Unknown") : child.status.charAt(0).toUpperCase() + child.status.slice(1)}
                </p>
              </div>
              <div>
                <p className="text-xs text-ink/50">Enrolled</p>
                <p className="text-sm font-medium text-ink">{formatDate(child.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs text-ink/50">Full name</p>
                <p className="text-sm font-medium text-ink">{child.firstName} {child.lastName}</p>
              </div>
              <div>
                <p className="text-xs text-ink/50">Preferred name</p>
                <p className="text-sm font-medium text-ink">{child.firstName}</p>
              </div>
              <div>
                <p className="text-xs text-ink/50">Date of birth</p>
                <p className="text-sm font-medium text-ink">{formatDate(child.dateOfBirth)}</p>
              </div>
              <div>
                <p className="text-xs text-ink/50">Gender</p>
                <p className="text-sm font-medium text-ink capitalize">{child.gender}</p>
              </div>
              <div>
                <p className="text-xs text-ink/50">State</p>
                <p className="text-sm font-medium text-ink">{child.state}</p>
              </div>
              <div>
                <p className="text-xs text-ink/50">LGA</p>
                <p className="text-sm font-medium text-ink">{child.lga || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-ink/50">Child Code</p>
                <p className="text-sm font-medium text-ink">{child.code}</p>
              </div>
            </div>
          </div>

          {linkedSchool && (
            <div className="rounded-[10px] border border-gold-500 bg-white p-5">
              <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">School</h2>
              <button
                type="button"
                onClick={() => navigate(`/admin/schools/${linkedSchool.id}`)}
                className="mt-4 flex w-full items-center gap-3 rounded-lg border border-gold-500/30 p-3 text-left transition-colors hover:bg-cream-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-700/30"
              >
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-forest-950 text-xs font-bold text-white">
                  {linkedSchool.code.replace("SCH-", "")}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink">{linkedSchool.name}</span>
                  <span className="mt-1 block truncate text-xs text-ink/50">
                    {linkedSchool.code} · {linkedSchool.city}, {linkedSchool.state}
                  </span>
                </span>
                <ExternalLinkIcon className="h-4 w-4 flex-none text-ink/40" />
              </button>
            </div>
          )}

          {/* Guardian & Family */}
          <GuardianFamilyCard child={child} />

          {/* Health & Wellbeing */}
          <div className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Health & Wellbeing</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="text-xs text-ink/50">Blood group</p>
                <p className="text-sm font-medium text-ink">—</p>
              </div>
              <div>
                <p className="text-xs text-ink/50">Allergies</p>
                <p className="text-sm font-medium text-ink">None reported</p>
              </div>
              <div>
                <p className="text-xs text-ink/50">Conditions</p>
                <p className="text-sm font-medium text-ink">None</p>
              </div>
              <div>
                <p className="text-xs text-ink/50">Last Check-up</p>
                <p className="text-sm font-medium text-ink">—</p>
              </div>
              <div>
                <p className="text-xs text-ink/50">Immunizations</p>
                <p className="text-sm font-medium text-ink">Up to date</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "academics" && renderAcademicTerms()}

      {((selectedAcademicTerm: AcademicTerm, child: Child) => {
        return false && activeTab === "academics" && (
        <div className="space-y-6">
          <div className="rounded-[10px] border border-gold-500 bg-white p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Academic Terms</h2>
                <p className="mt-1 text-sm text-ink/60">Results belong to a specific school session and term.</p>
              </div>
              {canMutate && (
                <button
                  type="button"
                  onClick={() => setShowTermForm((current) => !current)}
                  className="rounded-full bg-forest-950 px-4 py-2 text-sm font-medium text-white"
                >
                  {showTermForm ? "Cancel" : "New term"}
                </button>
              )}
            </div>

            {showTermForm && (
              <div className="mt-4 rounded-lg bg-cream-100 p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-ink/60">School session</span>
                    <input
                      value={termForm.session}
                      onChange={(event) => setTermForm((current) => ({ ...current, session: event.target.value }))}
                      placeholder="2025/2026"
                      className="w-full rounded-lg border border-gold-500 bg-white px-3 py-2 text-sm text-ink focus:border-forest-700 focus:outline-none"
                    />
                  </label>
                  <Select
                    label="Term"
                    value={termForm.term}
                    onChange={(term) => setTermForm((current) => ({ ...current, term }))}
                    options={["First Term", "Second Term", "Third Term"]}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void handleCreateAcademicTerm()}
                  disabled={savingTerm || !termForm.session.trim()}
                  className="mt-3 rounded-full bg-forest-950 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  {savingTerm ? "Creating..." : "Create term"}
                </button>
              </div>
            )}

            {academicTerms.length === 0 ? (
              <p className="mt-4 rounded-lg bg-cream-100 p-3 text-sm text-ink/60">No academic terms recorded yet.</p>
            ) : (
              <div className="mt-4 grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                <Select
                  label="Selected term"
                  value={selectedAcademicTerm ? `${selectedAcademicTerm.session} · ${selectedAcademicTerm.term}` : ""}
                  onChange={(value) => {
                    const term = academicTerms.find((item) => `${item.session} · ${item.term}` === value)
                    setSelectedAcademicTermId(term?.id ?? "")
                  }}
                  options={academicTerms.map((term) => `${term.session} · ${term.term}`)}
                  searchable
                  searchPlaceholder="Search terms..."
                />
                {selectedAcademicTerm && (
                  <div className="grid grid-cols-2 gap-3 sm:min-w-[260px]">
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-ink/60">Attendance %</span>
                      <input
                        key={`${selectedAcademicTerm.id}-attendance`}
                        type="number"
                        min="0"
                        max="100"
                        defaultValue={selectedTermDraft?.attendance}
                        onBlur={(event) => {
                          const value = event.target.value.trim() === "" ? null : Number(event.target.value)
                          if (value === null || (Number.isInteger(value) && value >= 0 && value <= 100)) void handleUpdateAcademicTerm(selectedAcademicTerm, { attendance: value })
                        }}
                        className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2 text-sm text-ink focus:border-forest-700 focus:outline-none"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-ink/60">Position</span>
                      <input
                        key={`${selectedAcademicTerm.id}-position`}
                        defaultValue={selectedTermDraft?.position}
                        onBlur={(event) => void handleUpdateAcademicTerm(selectedAcademicTerm, { position: event.target.value || null })}
                        className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2 text-sm text-ink focus:border-forest-700 focus:outline-none"
                      />
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedAcademicTerm && (
            <div className="rounded-[10px] border border-gold-500 bg-white p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">{selectedAcademicTerm.session} · {selectedAcademicTerm.term}</h2>
                  <p className="mt-1 text-sm text-ink/60">{child.school || NO_SCHOOL} · {child.classLevel} · Average: {averageScore === null ? "—" : `${averageScore} (${averageAssessment.grade})`}</p>
                </div>
                <p className="text-sm text-ink/60">{selectedAcademicTerm.results.length} subject{selectedAcademicTerm.results.length === 1 ? "" : "s"}</p>
              </div>

              {canMutate && (
                <div className="mt-5 rounded-lg bg-cream-100 p-4">
                  <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                    <Select
                      label="Add subject to this term"
                      value={subjects.find((subject) => subject.id === selectedAcademicSubjectId)?.name ?? ""}
                      onChange={(name) => setSelectedAcademicSubjectId(subjects.find((subject) => subject.name === name)?.id ?? "")}
                      options={subjects.filter((subject) => subject.active && !selectedAcademicTerm.results.some((result) => result.subject.id === subject.id)).map((subject) => subject.name)}
                      placeholder="Select an existing subject"
                      searchable
                      searchPlaceholder="Search subjects..."
                    />
                    <button
                      type="button"
                      onClick={() => void handleAddSubjectResult()}
                      disabled={!selectedAcademicSubjectId}
                      className="rounded-full bg-forest-950 px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50"
                    >
                      Add result
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowNewAcademicSubject((current) => !current)}
                    className="mt-3 text-sm font-medium text-forest-700 hover:underline"
                  >
                    {showNewAcademicSubject ? "Use existing subject" : "Subject is not in the catalog? Create it"}
                  </button>
                  {showNewAcademicSubject && (
                    <div className="mt-3 flex gap-2">
                      <input
                        value={newSubjectName}
                        onChange={(event) => setNewSubjectName(event.target.value)}
                        placeholder="New subject name"
                        className="min-w-0 flex-1 rounded-lg border border-gold-500 bg-white px-3 py-2 text-sm text-ink focus:border-forest-700 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => void handleCreateSubject()}
                        disabled={!newSubjectName.trim()}
                        className="rounded-full border border-forest-700 px-4 py-2 text-sm font-medium text-forest-700 disabled:opacity-50"
                      >
                        Create subject
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-5 space-y-4">
                {selectedAcademicTerm.results.length === 0 ? (
                  <p className="rounded-lg bg-cream-100 p-3 text-sm text-ink/60">No subjects recorded for this term.</p>
                ) : selectedAcademicTerm.results.map((result) => {
                  const draft = resultDrafts[result.id] ?? { score: result.score?.toString() ?? "", strengths: result.strengths ?? "", needsAttention: result.needsAttention ?? "", teacherNote: result.teacherNote ?? "" }
                  const draftScore = draft.score.trim() === "" ? null : Number(draft.score)
                  const assessment = getAcademicAssessment(draftScore !== null && Number.isFinite(draftScore) ? draftScore : null)
                  return (
                    <div key={result.id} className="rounded-lg border border-gold-500/40 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h3 className="font-medium text-ink">{result.subject.name}</h3>
                          <p className="text-xs text-ink/50">{result.score === null ? "Not yet scored" : `${result.score}/100 · ${result.grade} · ${result.proficiency}`}</p>
                        </div>
                        {canMutate && <button type="button" onClick={() => void handleDeleteSubjectResult(result)} className="text-xs text-red-600 hover:underline">Remove</button>}
                      </div>
                      <div className="mt-3 grid gap-3 sm:grid-cols-3">
                        <label className="block">
                          <span className="mb-1 block text-xs font-medium text-ink/60">Score</span>
                          <input type="number" min="0" max="100" step="1" value={draft.score} disabled={!canMutate} onChange={(event) => setResultDrafts((current) => ({ ...current, [result.id]: { ...draft, score: event.target.value } }))} placeholder="0-100" className="w-full rounded-lg border border-gold-500 bg-cream-50 px-3 py-2 text-sm text-ink focus:border-forest-700 focus:outline-none disabled:opacity-60" />
                        </label>
                        <div className="rounded-lg bg-cream-100 p-3"><p className="text-xs font-medium text-ink/60">Derived grade</p><p className="mt-1 text-lg font-semibold text-ink">{assessment.grade ?? "—"}</p></div>
                        <div className="rounded-lg bg-cream-100 p-3"><p className="text-xs font-medium text-ink/60">Derived proficiency</p><p className="mt-1 text-lg font-semibold text-ink">{assessment.proficiency ?? "—"}</p></div>
                        {(["strengths", "needsAttention", "teacherNote"] as const).map((field) => (
                          <label key={field} className="block sm:col-span-2">
                            <span className="mb-1 block text-xs font-medium capitalize text-ink/60">{field.replace(/([A-Z])/g, " $1")}</span>
                            <textarea value={draft[field]} disabled={!canMutate} onChange={(event) => setResultDrafts((current) => ({ ...current, [result.id]: { ...draft, [field]: event.target.value } }))} rows={2} className="w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-3 py-2 text-sm text-ink focus:border-forest-700 focus:outline-none disabled:opacity-60" />
                          </label>
                        ))}
                      </div>
                      {canMutate && <button type="button" onClick={() => void handleSaveSubjectResult(result)} className="mt-3 rounded-full bg-forest-950 px-4 py-2 text-sm font-medium text-white">Save result</button>}
                    </div>
                  )
                })}
              </div>

              {canMutate && (
                <div className="mt-5 border-t border-gold-500/30 pt-5">
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-ink/60">Teacher summary</span>
                    <textarea
                      value={selectedTermDraft?.teacherSummary ?? ""}
                      onChange={(event) => setTermDrafts((current) => ({ ...current, [selectedAcademicTerm.id]: { ...selectedTermDraft!, teacherSummary: event.target.value } }))}
                      rows={3}
                      className="w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-3 py-2 text-sm text-ink focus:border-forest-700 focus:outline-none"
                    />
                  </label>
                  <button type="button" onClick={() => void handleUpdateAcademicTerm(selectedAcademicTerm, { teacherSummary: selectedTermDraft?.teacherSummary || null })} className="mt-3 rounded-full border border-forest-700 px-4 py-2 text-sm font-medium text-forest-700">Save summary</button>
                </div>
              )}
            </div>
          )}

          <div className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Term History</h2>
            <div className="mt-4 space-y-2">
              {academicTerms.length === 0 ? <p className="rounded-lg bg-cream-100 p-3 text-sm text-ink/60">Create a term to begin recording academic results.</p> : academicTerms.map((term) => (
                <button key={term.id} type="button" onClick={() => setSelectedAcademicTermId(term.id)} className={`flex w-full items-center justify-between rounded-lg border p-3 text-left ${term.id === selectedAcademicTermId ? "border-forest-700 bg-cream-100" : "border-gold-500/30"}`}>
                  <span className="text-sm font-medium text-ink">{term.session} · {term.term}</span>
                  <span className="text-xs text-ink/50">{term.results.length} subjects</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        )
      })(selectedAcademicTerm!, child!)}

      {activeTab === "sponsorship" && (
        <div className="space-y-6">
          {/* Sponsorship Info */}
          <div className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Sponsorship</h2>
            {child.activeSponsors.length > 0 ? (
              <div className="mt-4">
                <div className="space-y-3">
                  {child.activeSponsors.map((sponsor) => (
                    <div key={sponsor.id} className="flex items-center gap-3 rounded-lg border border-gold-500/30 p-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest-950 text-sm font-bold text-white">
                        {sponsor.name.split(" ").map((name) => name[0]).join("").slice(0, 2)}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-ink">{sponsor.name}</p>
                        <p className="text-xs text-ink/50">Active sponsor · Since {formatDate(sponsor.matchedAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-ink/50">{child.activeSponsors.length} active sponsor{child.activeSponsors.length === 1 ? "" : "s"}</p>
                {canMutate && canMatchChild ? (
                  <button type="button" onClick={() => setShowSponsorMatchModal(true)} className="mt-3 rounded-full bg-forest-950 px-4 py-2 text-sm font-medium text-white hover:bg-forest-900">
                    Add sponsor
                  </button>
                ) : !canMatchChild ? (
                  <p className="mt-3 text-xs text-ink/60">Verify this child before matching with a sponsor.</p>
                ) : null}
              </div>
            ) : (
              <div className="mt-4 rounded-lg bg-cream-100 p-4 text-center">
                <p className="text-sm text-ink/70">This child is not currently sponsored.</p>
                {canMutate && canMatchChild ? (
                  <button type="button" onClick={() => setShowSponsorMatchModal(true)} className="mt-2 inline-flex items-center gap-2 rounded-full bg-forest-950 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-forest-900">
                    Match with sponsor
                  </button>
                ) : !canMatchChild ? (
                  <p className="mt-2 text-xs text-ink/60">Verify this child before matching with a sponsor.</p>
                ) : null}
              </div>
            )}
          </div>

          {/* Sponsorship Ledger */}
          <div className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Sponsorship Ledger</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs text-ink/50">Monthly commitment</p>
                <p className="text-sm font-medium text-ink">{child.status === "sponsored" ? "N50,000" : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-ink/50">Start date</p>
                <p className="text-sm font-medium text-ink">{child.sponsoredAt ? formatDate(child.sponsoredAt) : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-ink/50">Total received</p>
                <p className="text-sm font-medium text-ink">{child.status === "sponsored" ? "N720,000" : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-ink/50">Next disbursement</p>
                <p className="text-sm font-medium text-ink">—</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-lg border border-gold-500/30 p-3">
              <span className="text-sm text-ink">Sponsor letters exchanged</span>
              <span className="text-sm text-ink/50">0</span>
            </div>
          </div>
        </div>
      )}

      {activeTab === "documents" && (
        <div className="space-y-6">
          <div className="rounded-[10px] border border-gold-500 bg-white p-5">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Documents on File</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gold-500/50">
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">Name</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">Kind</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">Uploaded</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">Size</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gold-500/30">
                  {child.documents.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-sm text-ink/50">
                        No documents uploaded.
                      </td>
                    </tr>
                  ) : child.documents.map((document) => (
                    <tr key={document.key}>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <FileIcon className="h-4 w-4 text-ink/40" />
                          <span className="text-sm text-ink">{document.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm capitalize text-ink/60">{document.purpose.replace("child-", "")}</td>
                      <td className="px-3 py-3 text-sm text-ink/60">{formatDate(document.uploadedAt)}</td>
                      <td className="px-3 py-3 text-sm text-ink/60">{(document.sizeBytes / 1024 / 1024).toFixed(1)} MB</td>
                      <td className="px-3 py-3">
                        <a
                          href={document.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-md border border-ink/20 px-3 py-1 text-xs font-medium text-ink transition-colors hover:bg-cream-100"
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === "timeline" && (
        <div className="space-y-6">
          <ActivityTimeline
            entityType={AdminActivityEntityType.Child}
            entityId={child.id}
            title="Timeline"
            refreshKey={activityRefreshKey}
            fallback={
              <div className="rounded-[10px] border border-gold-500 bg-white p-5">
                <h2 className="font-display text-sm font-bold uppercase tracking-wider text-forest-700">Timeline</h2>
                <div className="mt-4 space-y-4">
                  {child.sponsoredAt && (
                    <div className="flex items-start gap-3">
                      <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-green-500" />
                      <div>
                        <p className="text-sm font-medium text-ink">Matched with sponsor</p>
                        <p className="text-xs text-ink/50">{formatDate(child.sponsoredAt)} · Sponsored by {child.sponsorName}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                    <div>
                      <p className="text-sm font-medium text-ink">Status changed to {child.status}</p>
                      <p className="text-xs text-ink/50">{formatDate(child.updatedAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-forest-700" />
                    <div>
                      <p className="text-sm font-medium text-ink">Child enrolled in program</p>
                      <p className="text-xs text-ink/50">{formatDate(child.createdAt)} · {child.nominatedBy ? `Nominated by ${child.nominatedBy}` : "Direct enrollment"}</p>
                    </div>
                  </div>
                </div>
              </div>
            }
          />
        </div>
      )}

      <Modal
        isOpen={showTermForm}
        onClose={() => setShowTermForm(false)}
        title="Add academic term"
        subtitle="Create a session and term before recording subjects."
        maxWidth="md"
      >
        <div className="space-y-4">
          <Select
            label="School session"
            value={termForm.session}
            onChange={(session) => setTermForm((current) => ({ ...current, session }))}
            options={ACADEMIC_SESSIONS}
          />
          <Select
            label="Term"
            value={termForm.term}
            onChange={(term) => setTermForm((current) => ({ ...current, term }))}
            options={["First Term", "Second Term", "Third Term"]}
          />
        </div>
        <ModalActions>
          <ModalButton onClick={() => setShowTermForm(false)}>Cancel</ModalButton>
          <ModalButton type="button" variant="primary" onClick={() => void handleCreateAcademicTerm()} disabled={savingTerm || !termForm.session}>
            {savingTerm ? "Creating..." : "Create term"}
          </ModalButton>
        </ModalActions>
      </Modal>

      <Modal
        isOpen={editingAcademicTerm !== null}
        onClose={() => setEditingAcademicTermId(null)}
        title="Edit academic term"
        subtitle={editingAcademicTerm ? `${editingAcademicTerm.session} · ${editingAcademicTerm.term}` : undefined}
        maxWidth="md"
      >
        {editingAcademicTerm && (() => {
          const draft = termDrafts[editingAcademicTerm.id] ?? { attendance: "", position: "", teacherSummary: "" }
          return (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">Attendance %</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={draft.attendance}
                    onChange={(event) => setTermDrafts((current) => ({ ...current, [editingAcademicTerm.id]: { ...draft, attendance: event.target.value } }))}
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink focus:border-forest-700 focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">Position</span>
                  <input
                    value={draft.position}
                    onChange={(event) => setTermDrafts((current) => ({ ...current, [editingAcademicTerm.id]: { ...draft, position: event.target.value } }))}
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink focus:border-forest-700 focus:outline-none"
                  />
                </label>
              </div>
              <label className="mt-4 block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Teacher summary</span>
                <textarea
                  value={draft.teacherSummary}
                  onChange={(event) => setTermDrafts((current) => ({ ...current, [editingAcademicTerm.id]: { ...draft, teacherSummary: event.target.value } }))}
                  rows={4}
                  className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink focus:border-forest-700 focus:outline-none"
                />
              </label>
              <ModalActions>
                <ModalButton onClick={() => setEditingAcademicTermId(null)}>Cancel</ModalButton>
                <ModalButton type="button" variant="primary" onClick={() => void handleSaveAcademicTerm()}>Save changes</ModalButton>
              </ModalActions>
            </>
          )
        })()}
      </Modal>

      <Modal
        isOpen={showResultModal}
        onClose={() => { setShowResultModal(false); setEditingResultId(null); setShowNewAcademicSubject(false) }}
        title={editingResult ? `Edit ${editingResult.subject.name}` : "Add subject result"}
        subtitle={selectedAcademicTerm ? `${selectedAcademicTerm.session} · ${selectedAcademicTerm.term}` : undefined}
        maxWidth="md"
      >
        {editingResult ? (() => {
          const draft = resultDrafts[editingResult.id] ?? { score: editingResult.score?.toString() ?? "", strengths: editingResult.strengths ?? "", needsAttention: editingResult.needsAttention ?? "", teacherNote: editingResult.teacherNote ?? "" }
          const draftScore = draft.score.trim() === "" ? null : Number(draft.score)
          const assessment = getAcademicAssessment(draftScore !== null && Number.isFinite(draftScore) ? draftScore : null)
          return (
            <>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Score</span>
                <input type="number" min="0" max="100" step="1" value={draft.score} onChange={(event) => setResultDrafts((current) => ({ ...current, [editingResult.id]: { ...draft, score: event.target.value } }))} placeholder="0-100" className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink focus:border-forest-700 focus:outline-none" />
              </label>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg bg-cream-100 p-3"><p className="text-xs font-medium text-ink/60">Derived grade</p><p className="mt-1 text-lg font-semibold text-ink">{assessment.grade ?? "—"}</p></div>
                <div className="rounded-lg bg-cream-100 p-3"><p className="text-xs font-medium text-ink/60">Derived proficiency</p><p className="mt-1 text-lg font-semibold text-ink">{assessment.proficiency ?? "—"}</p></div>
              </div>
              {(["strengths", "needsAttention", "teacherNote"] as const).map((field) => (
                <label key={field} className="mt-4 block">
                  <span className="mb-1.5 block text-sm font-medium capitalize text-ink">{field.replace(/([A-Z])/g, " $1")}</span>
                  <textarea value={draft[field]} onChange={(event) => setResultDrafts((current) => ({ ...current, [editingResult.id]: { ...draft, [field]: event.target.value } }))} rows={3} className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink focus:border-forest-700 focus:outline-none" />
                </label>
              ))}
              <ModalActions>
                <ModalButton onClick={() => setShowResultModal(false)}>Cancel</ModalButton>
                <ModalButton type="button" variant="primary" onClick={() => void handleSaveSubjectResult(editingResult)}>Save result</ModalButton>
              </ModalActions>
            </>
          )
        })() : (() => {
          const draftScore = addResultDraft.score.trim() === "" ? null : Number(addResultDraft.score)
          const assessment = getAcademicAssessment(draftScore !== null && Number.isFinite(draftScore) ? draftScore : null)
          return (
          <>
            <Select
              label="Subject"
              value={subjects.find((subject) => subject.id === selectedAcademicSubjectId)?.name ?? ""}
              onChange={(name) => setSelectedAcademicSubjectId(subjects.find((subject) => subject.name === name)?.id ?? "")}
              options={subjects.filter((subject) => subject.active && !selectedAcademicTerm?.results.some((result) => result.subject.id === subject.id)).map((subject) => subject.name)}
              placeholder="Select an existing subject"
              emptyMessage="No subjects available for this term. Create a new subject first."
              searchable
              searchPlaceholder="Search subjects..."
            />
            <button type="button" onClick={() => setShowNewAcademicSubject((current) => !current)} className="mt-3 text-sm font-medium text-forest-700 hover:underline">
              {showNewAcademicSubject ? "Use existing subject" : "Subject not in the catalog? Create it"}
            </button>
            {showNewAcademicSubject && (
              <div className="mt-3 flex gap-2">
                <input value={newSubjectName} onChange={(event) => setNewSubjectName(event.target.value)} placeholder="New subject name" className="min-w-0 flex-1 rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink focus:border-forest-700 focus:outline-none" />
                <button type="button" onClick={() => void handleCreateSubject()} disabled={!newSubjectName.trim()} className="rounded-full border border-forest-700 px-4 py-2 text-sm font-medium text-forest-700 disabled:opacity-50">Create subject</button>
              </div>
            )}
            <label className="mt-5 block">
              <span className="mb-1.5 block text-sm font-medium text-ink">Score *</span>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={addResultDraft.score}
                onChange={(event) => setAddResultDraft((current) => ({ ...current, score: event.target.value }))}
                placeholder="0-100"
                className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink focus:border-forest-700 focus:outline-none"
              />
              <span className="mt-1 block text-xs text-ink/50">Enter a whole number from 0 to 100.</span>
            </label>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg bg-cream-100 p-3">
                <p className="text-xs font-medium text-ink/60">Derived grade</p>
                <p className="mt-1 text-lg font-semibold text-ink">{assessment.grade ?? "—"}</p>
              </div>
              <div className="rounded-lg bg-cream-100 p-3">
                <p className="text-xs font-medium text-ink/60">Derived proficiency</p>
                <p className="mt-1 text-lg font-semibold text-ink">{assessment.proficiency ?? "—"}</p>
              </div>
            </div>
            {(["strengths", "needsAttention", "teacherNote"] as const).map((field) => (
              <label key={field} className="mt-4 block">
                <span className="mb-1.5 block text-sm font-medium capitalize text-ink">{field.replace(/([A-Z])/g, " $1")}</span>
                <textarea
                  value={addResultDraft[field]}
                  onChange={(event) => setAddResultDraft((current) => ({ ...current, [field]: event.target.value }))}
                  rows={3}
                  className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink focus:border-forest-700 focus:outline-none"
                />
              </label>
            ))}
            <ModalActions>
              <ModalButton onClick={() => setShowResultModal(false)}>Cancel</ModalButton>
              <ModalButton type="button" variant="primary" onClick={() => void handleAddSubjectResult()} disabled={!selectedAcademicSubjectId || !addResultDraft.score.trim()}>Add result</ModalButton>
            </ModalActions>
          </>
          )
        })()}
      </Modal>

      <ConfirmModal
        open={resultToDelete !== null}
        title="Remove subject result?"
        message={resultToDelete ? `Remove ${resultToDelete.subject.name} from this term?` : ""}
        confirmLabel="Remove result"
        onConfirm={() => resultToDelete && void handleDeleteSubjectResult(resultToDelete)}
        onCancel={() => setResultToDelete(null)}
      />

      <SponsorMatchModal
        open={showSponsorMatchModal}
        onClose={() => setShowSponsorMatchModal(false)}
        childId={child.id}
        onMatched={() => void reloadPageData()}
      />

      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title="Update child status"
        subtitle="Choose the current stage of this child's enrollment."
        maxWidth="sm"
      >
        <div className="grid gap-2 sm:grid-cols-2">
          {(["nominated", "unverified", "verified", "sponsored", "graduated"] as ChildStatus[]).map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setPendingStatus(status)}
              className={`rounded-lg border px-4 py-3 text-left text-sm font-medium capitalize transition-colors ${
                pendingStatus === status
                  ? "border-forest-700 bg-forest-50 text-forest-900"
                  : "border-gold-500/50 text-ink hover:bg-cream-100"
              }`}
            >
              {status}
            </button>
          ))}
        </div>
        <ModalActions>
          <ModalButton onClick={() => setShowStatusModal(false)}>Cancel</ModalButton>
          <ModalButton
            type="button"
            variant="primary"
            onClick={() => {
              setShowStatusModal(false)
              void handleStatusChange(pendingStatus)
            }}
            disabled={saving || pendingStatus === child.status}
          >
            {saving ? "Updating..." : "Update status"}
          </ModalButton>
        </ModalActions>
      </Modal>

      {/* Edit Modal */}
      {showEditModal && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
          <div
            ref={modalRef}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
          >
            <h2 className="font-display text-xl font-bold text-ink">Edit Child Record</h2>
            <p className="mt-1 text-sm text-ink/60">{child.code} · {child.firstName} {child.lastName}</p>
            <form onSubmit={handleEditSubmit} className="mt-6 space-y-4">
              <div className="flex items-center gap-4 rounded-lg bg-cream-100 p-3">
                {editPhotoPreview || child.photoUrl ? (
                  <img src={editPhotoPreview ?? child.photoUrl ?? ""} alt="" className="h-14 w-14 rounded-full object-cover" />
                ) : (
                  <div className="h-14 w-14 rounded-full bg-forest-950" />
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium text-ink">Profile photo</p>
                  <p className="text-xs text-ink/60">{editPhotoUploading ? "Uploading..." : editPhotoKey ? "New photo uploaded" : "Keep current photo"}</p>
                </div>
                <input ref={editPhotoInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleEditPhotoChange} className="hidden" />
                <button
                  type="button"
                  onClick={() => editPhotoInputRef.current?.click()}
                  disabled={editPhotoUploading}
                  className="rounded-full border border-forest-700 px-3 py-2 text-xs font-medium text-forest-700 disabled:opacity-50"
                >
                  Change photo
                </button>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">First Name *</span>
                  <input
                    type="text"
                    required
                    value={editForm.firstName}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">Last Name *</span>
                  <input
                    type="text"
                    required
                    value={editForm.lastName}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  />
                </label>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">Preferred Name</span>
                  <input
                    value={editForm.preferredName}
                    onChange={(e) => setEditForm({ ...editForm, preferredName: e.target.value })}
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink focus:border-forest-700 focus:outline-none"
                  />
                </label>
                <Select
                  label="Status"
                  value={editForm.status.charAt(0).toUpperCase() + editForm.status.slice(1)}
                  onChange={(value) => setEditForm({ ...editForm, status: value.toLowerCase() as ChildStatus })}
                  options={["Nominated", "Unverified", "Verified", "Sponsored", "Graduated"]}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <DatePicker
                  label="Date of Birth *"
                  value={editForm.dateOfBirth}
                  onChange={(value) => setEditForm({ ...editForm, dateOfBirth: value })}
                  max={new Date()}
                />
                <Select
                  label="Gender *"
                  value={editForm.gender === "male" ? "Male" : "Female"}
                  onChange={(value) => setEditForm({ ...editForm, gender: value.toLowerCase() })}
                  options={["Male", "Female"]}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Select
                  label="Class Level *"
                  value={editForm.classLevel}
                  onChange={(value) => setEditForm({ ...editForm, classLevel: value })}
                  options={CLASS_LEVELS}
                />
                <Select
                  label="School"
                  value={selectedSchoolLabel}
                  onChange={(value) => {
                    if (value === NO_SCHOOL) {
                      setEditForm({ ...editForm, school: "", schoolId: null })
                      return
                    }
                    const nextSchool = schools.find((school) => `${school.code} · ${school.name}` === value)
                    if (nextSchool) setEditForm({ ...editForm, school: nextSchool.name, schoolId: nextSchool.id })
                  }}
                   options={schoolOptions}
                   placeholder="No school assigned"
                   emptyMessage="No schools available."
                   searchable
                   searchPlaceholder="Search schools..."
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Select
                  label="State *"
                  value={editForm.state}
                  onChange={(value) => setEditForm({ ...editForm, state: value })}
                  options={NIGERIAN_STATES}
                />
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">LGA</span>
                  <input
                    type="text"
                    value={editForm.lga}
                    onChange={(e) => setEditForm({ ...editForm, lga: e.target.value })}
                    className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  />
                </label>
              </div>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Notes</span>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                  className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Family circumstances, special notes..."
                />
              </label>

              <div className="rounded-lg border border-gold-500/40 p-4">
                <p className="text-sm font-medium text-ink">Nominator and guardian</p>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  {([
                    ["nominatedBy", "Nominated by"],
                    ["nominatorContact", "Nominator contact"],
                    ["guardianName", "Guardian name"],
                    ["guardianPhone", "Guardian phone"],
                    ["guardianEmail", "Guardian email"],
                    ["guardianRelationship", "Guardian relationship"],
                    ["householdSize", "Household size"],
                    ["address", "Address"],
                  ] as const).map(([field, label]) => (
                    <label key={field} className="block">
                      <span className="mb-1.5 block text-sm font-medium text-ink">{label}</span>
                      <input
                        type={field === "guardianEmail" ? "email" : field === "householdSize" ? "number" : "text"}
                        value={editForm[field]}
                        onChange={(e) => setEditForm({ ...editForm, [field]: e.target.value })}
                        className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink focus:border-forest-700 focus:outline-none"
                      />
                    </label>
                  ))}
                </div>
                <label className="mt-4 block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">Family circumstances</span>
                  <textarea
                    value={editForm.familyCircumstances}
                    onChange={(e) => setEditForm({ ...editForm, familyCircumstances: e.target.value })}
                    rows={3}
                    className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink focus:border-forest-700 focus:outline-none"
                  />
                </label>
                <label className="mt-4 block">
                  <span className="mb-1.5 block text-sm font-medium text-ink">Sponsorship notes</span>
                  <textarea
                    value={editForm.sponsorshipNotes}
                    onChange={(e) => setEditForm({ ...editForm, sponsorshipNotes: e.target.value })}
                    rows={3}
                    className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink focus:border-forest-700 focus:outline-none"
                  />
                </label>
                <label className="mt-4 flex items-center gap-3 text-sm font-medium text-ink">
                  <input
                    type="checkbox"
                    checked={editForm.needsSponsorship}
                    onChange={(e) => setEditForm({ ...editForm, needsSponsorship: e.target.checked })}
                    className="h-4 w-4 rounded border-gold-500 text-forest-700"
                  />
                  Needs sponsorship
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    if (editPhotoPreview) URL.revokeObjectURL(editPhotoPreview)
                    setEditPhotoPreview(null)
                    setEditPhotoKey(null)
                    setShowEditModal(false)
                  }}
                  className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
          </div>
        </Portal>
      )}

      {/* Message Guardian Modal */}
      {showMessageModal && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
          <div
            ref={modalRef}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
          >
            <h2 className="font-display text-xl font-bold text-ink">Message Guardian</h2>
            <p className="mt-1 text-sm text-ink/60">{child.code} · {child.firstName} {child.lastName}</p>
            <form className="mt-6 space-y-4" onSubmit={handleMessageSubmit}>
              <Select
                label="Send via"
                value="Email"
                onChange={() => {}}
                options={["Email"]}
                disabled
              />

              {!child.guardianEmail && (
                <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Add a guardian email on the profile tab before sending a message.
                </p>
              )}

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Subject</span>
                <input
                  type="text"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                  required
                  disabled={sendingMessage}
                  className="block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none disabled:opacity-60"
                  placeholder="Message subject"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink">Message</span>
                <textarea
                  rows={5}
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                  required
                  disabled={sendingMessage}
                  className="block w-full resize-none rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none disabled:opacity-60"
                  placeholder={`Hi ${child.nominatedBy?.split(" ")[0] || "there"},\n\nI'm reaching out regarding ${child.firstName}...`}
                />
              </label>

              <div className="flex justify-start gap-3 pt-2">
                <button
                  type="submit"
                  disabled={sendingMessage || !child.guardianEmail}
                  className="rounded-full bg-forest-950 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sendingMessage ? "Sending..." : "Send message"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowMessageModal(false)}
                  className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
          </div>
        </Portal>
      )}

      {/* Upload Update Modal */}
      {showUploadModal && (
        <Portal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/30 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
          <div
            ref={modalRef}
            className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-[20px] border border-gold-500 bg-white p-6 shadow-xl"
          >
            <h2 className="font-display text-xl font-bold text-ink">Upload Update</h2>
            <p className="mt-1 text-sm text-ink/60">{child.firstName} {child.lastName}</p>
            <div className="mt-6 space-y-4">
              <Select
                label="Upload Update"
                value={uploadForm.updateType}
                onChange={(value) => setUploadForm({ ...uploadForm, updateType: value })}
                options={UPDATE_TYPES}
              />

              <div>
                <span className="mb-1.5 block text-sm font-medium text-ink">Attachment</span>
                <input
                  ref={uploadInputRef}
                  type="file"
                  onChange={handleUploadFileChange}
                  className="hidden"
                />
                <div
                  onClick={() => uploadInputRef.current?.click()}
                  className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gold-500 bg-cream-50 p-6 transition-colors hover:border-forest-700 hover:bg-cream-100"
                >
                  {uploading ? (
                    <div className="text-center">
                      <UploadIcon className="mx-auto h-8 w-8 animate-pulse text-forest-700" />
                      <p className="mt-2 text-sm font-medium text-ink">Uploading {uploadForm.fileName}...</p>
                    </div>
                  ) : uploadForm.fileName ? (
                    <div className="text-center">
                      <FileIcon className="mx-auto h-8 w-8 text-forest-700" />
                      <p className="mt-2 text-sm font-medium text-ink">{uploadForm.fileName}</p>
                      <p className="text-xs text-ink/50">Click to change file</p>
                    </div>
                  ) : (
                    <>
                      <UploadIcon className="h-8 w-8 text-ink/30" />
                      <p className="mt-2 text-sm text-ink/60">Click to select a file</p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowUploadModal(false)
                    setUploadForm({ updateType: "Term report", fileName: "" })
                  }}
                  className="rounded-full border border-ink/20 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
          </div>
        </Portal>
      )}
    </div>
  )
}
